import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type JSX } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { formatKpiUnit } from '@/components/kpis/KpiUnitDisplay';
import { LogsheetEntryForm } from '@/components/logsheets-maker/LogsheetEntryForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMakerKpisForWorkOrderMachineQuery } from '@/hooks/useLogsheetMakerQueries';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import { formatMachineName } from '@/lib/utils/machine-label';
import {
  getLogsheetMakerRequest,
  submitLogsheetMakerRequest,
  updateLogsheetMakerRequest,
} from '@/services/logsheets-maker';
import { getMachineTypeRequest } from '@/services/machine-types';
import type {
  CreateLogsheetMakerBody,
  KpiValuesShape,
  LogsheetMakerDetail,
  MakerKpiForMachine,
  UpdateLogsheetMakerBody,
} from '@/types/logsheets-maker';
import type { MachineTypeDetail } from '@/types/machine-types';

/**
 * Plan 12-03 — MakerLogsheetEditPage
 *
 * Edit page for OWN Draft|Rejected entries (D-107). Hydrates the entry +
 * its machine type schema, then renders LogsheetEntryForm in edit mode.
 *
 *  - Save Draft → updateLogsheetMakerRequest (no status change).
 *  - Submit     → updateLogsheetMakerRequest THEN submitLogsheetMakerRequest
 *                 (two-step: persist edits, then transition status).
 *
 * Site/Machine/Type are READ-ONLY in the form summary panel (D-107 snapshot
 * — backend rejects mutation either way; the UI matches that contract).
 */

export function MakerLogsheetEditPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // D-162: WO-scoped KPI values live outside the form (matches the create
  // wizard's pattern) — keyed by `kpi:{id}`. Seeded from `initial.kpiValues`
  // when the entry hydrates, then mutated by the kpi-slot inputs below.
  const [kpiValues, setKpiValues] = useState<Record<string, string>>({});
  const [kpiValuesError, setKpiValuesError] = useState<string | null>(null);

  const detailQuery = useQuery<LogsheetMakerDetail, ApiError>({
    queryKey: ['logsheet-maker', id],
    queryFn: () => getLogsheetMakerRequest(id),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 5_000,
  });

  // Seed local KPI state from the hydrated entry. Stringify numerics so the
  // <input type="number"> bindings stay controlled.
  useEffect(() => {
    if (detailQuery.data === undefined) return;
    const seeded: Record<string, string> = {};
    for (const [k, v] of Object.entries(detailQuery.data.kpiValues)) {
      seeded[k] = v === null || v === undefined ? '' : String(v);
    }
    setKpiValues(seeded);
    setKpiValuesError(null);
  }, [detailQuery.data?.id]);

  const machineTypeId = detailQuery.data?.machineTypeId ?? 0;
  const workOrderId = detailQuery.data?.workOrderId ?? null;
  const machineId = detailQuery.data?.machineId ?? null;
  const useWoKpis = workOrderId !== null;

  const machineTypeQuery = useQuery<MachineTypeDetail, ApiError>({
    queryKey: ['machine-type', machineTypeId],
    // Only needed for legacy no-WO entries — the WO-KPI path uses an empty
    // schema and renders inputs from the WO KPIs hook instead.
    queryFn: () => getMachineTypeRequest(machineTypeId),
    enabled: machineTypeId > 0 && !useWoKpis,
    staleTime: 30_000,
  });

  const makerKpisQuery = useMakerKpisForWorkOrderMachineQuery(workOrderId, machineId);
  const makerKpis: readonly MakerKpiForMachine[] = makerKpisQuery.data ?? [];

  function validateWoKpiValues(): string | null {
    for (const kpi of makerKpis) {
      const raw = kpiValues[`kpi:${String(kpi.id)}`];
      if (raw === undefined || raw.trim() === '') {
        return `${kpi.name}: required numeric value`;
      }
      const num = Number(raw);
      if (!Number.isFinite(num)) {
        return `${kpi.name}: must be a valid number`;
      }
    }
    return null;
  }

  const updateMutation = useMutation<
    LogsheetMakerDetail,
    ApiError,
    { body: UpdateLogsheetMakerBody; thenSubmit: boolean }
  >({
    mutationFn: async ({ body, thenSubmit }) => {
      const updated = await updateLogsheetMakerRequest(id, body);
      if (thenSubmit) {
        return await submitLogsheetMakerRequest(id);
      }
      return updated;
    },
    onSuccess: (result, variables) => {
      const verb = variables.thenSubmit ? 'submitted' : 'saved';
      toast.success(`Logsheet ${verb}`);
      void queryClient.invalidateQueries({ queryKey: ['logsheets-maker'] });
      void queryClient.invalidateQueries({ queryKey: ['logsheet-maker', id] });
      void navigate(`/logsheets/view/${String(result.id)}`);
    },
    onError: (err) => {
      setErrorMessage(err.detail ?? err.message);
      toast.error('Could not save logsheet', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('logsheet.maker.update')) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!Number.isFinite(id) || id <= 0) {
    return <Navigate to="/logsheets/maker" replace />;
  }

  /**
   * When the entry is tied to a Work Order, replace whatever `kpiValues` the
   * form's react-hook-form state holds (which is empty in WO mode, since the
   * form's inner KPI section is hidden) with the WO-KPI values held in this
   * page's local state. The values are coerced to numbers so the wire shape
   * matches what the create wizard sends.
   */
  function resolveKpiValuesForBody(formKpiValues: KpiValuesShape): KpiValuesShape {
    if (!useWoKpis) return formKpiValues;
    return Object.fromEntries(
      Object.entries(kpiValues)
        .filter(([, v]) => v.trim() !== '')
        .map(([k, v]) => [k, Number(v)]),
    );
  }

  function handleSaveDraft(body: CreateLogsheetMakerBody | UpdateLogsheetMakerBody): void {
    setErrorMessage(null);
    setKpiValuesError(null);
    const update: UpdateLogsheetMakerBody = {
      entryDate: body.entryDate,
      shift: body.shift,
      startTime: body.startTime,
      endTime: body.endTime,
      workDescription: body.workDescription,
      idleReason: body.idleReason,
      notes: body.notes,
      kpiValues: resolveKpiValuesForBody(body.kpiValues),
    };
    updateMutation.mutate({ body: update, thenSubmit: false });
  }

  function handleSubmit(body: CreateLogsheetMakerBody | UpdateLogsheetMakerBody): void {
    setErrorMessage(null);
    setKpiValuesError(null);
    // Strict validation: a Submit through the WO path must have every KPI filled.
    if (useWoKpis && makerKpis.length > 0) {
      const err = validateWoKpiValues();
      if (err !== null) {
        setKpiValuesError(err);
        return;
      }
    }
    const update: UpdateLogsheetMakerBody = {
      entryDate: body.entryDate,
      shift: body.shift,
      startTime: body.startTime,
      endTime: body.endTime,
      workDescription: body.workDescription,
      idleReason: body.idleReason,
      notes: body.notes,
      kpiValues: resolveKpiValuesForBody(body.kpiValues),
    };
    updateMutation.mutate({ body: update, thenSubmit: true });
  }

  return (
    <div className="space-y-6" data-testid="maker-edit-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Edit logsheet</h1>
        <p className="text-muted-foreground">
          Site, machine, and entry type are locked at creation. Save Draft or Submit when ready.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Logsheet #{id}</CardTitle>
          <CardDescription>
            {detailQuery.data
              ? formatMachineName(
                  detailQuery.data.machineName,
                  detailQuery.data.vehicleNumber,
                  detailQuery.data.machineCode,
                )
              : 'Loading…'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {detailQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="maker-edit-loading">
              Loading logsheet…
            </p>
          )}
          {detailQuery.isError && (
            <p className="text-sm text-destructive" data-testid="maker-edit-error">
              Failed to load logsheet.
            </p>
          )}
          {detailQuery.data && (useWoKpis || machineTypeQuery.data) && (() => {
            const detail = detailQuery.data;
            // WO-path: render KPI inputs inline from the WO KPIs hook.
            // No-WO legacy path: leave hideKpiSection=false so the form uses
            // the machine-type custom-field schema (pre-D-162 behavior).
            const kpiSlot = useWoKpis ? (
              <div className="space-y-3" data-testid="maker-edit-kpi-wo-section">
                <Label className="text-sm font-medium">KPI values</Label>
                {makerKpisQuery.isLoading && (
                  <p className="text-sm text-muted-foreground" data-testid="maker-edit-kpi-loading">
                    Loading KPIs…
                  </p>
                )}
                {makerKpisQuery.isError && (
                  <p className="text-sm text-destructive" data-testid="maker-edit-kpi-error">
                    Failed to load KPIs for this work order.
                  </p>
                )}
                {!makerKpisQuery.isLoading && makerKpis.length === 0 && (
                  <p className="text-sm text-muted-foreground" data-testid="maker-edit-kpi-empty">
                    No KPIs defined for this Work Order.
                  </p>
                )}
                {makerKpis.length > 0 && (
                  <div className="space-y-3">
                    {makerKpis.map((kpi) => (
                      <div key={kpi.id} className="space-y-1">
                        <Label htmlFor={`maker-edit-kpi-${String(kpi.id)}`}>
                          {kpi.name}{' '}
                          <span className="text-xs text-muted-foreground">
                            ({formatKpiUnit(kpi.unit, kpi.customUnitText)})
                          </span>{' '}
                          <span className="text-xs text-destructive">*</span>
                        </Label>
                        <Input
                          id={`maker-edit-kpi-${String(kpi.id)}`}
                          data-testid={`maker-edit-kpi-input-${String(kpi.id)}`}
                          type="number"
                          value={kpiValues[`kpi:${String(kpi.id)}`] ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setKpiValuesError(null);
                            setKpiValues((prev) => ({
                              ...prev,
                              [`kpi:${String(kpi.id)}`]: val,
                            }));
                          }}
                          disabled={updateMutation.isPending}
                        />
                      </div>
                    ))}
                    {kpiValuesError !== null && (
                      <p
                        className="text-sm text-destructive"
                        data-testid="maker-edit-kpi-validation-error"
                      >
                        {kpiValuesError}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : null;

            return (
              <LogsheetEntryForm
                mode="edit"
                initial={detail}
                context={{
                  siteId: detail.siteId,
                  siteName: detail.siteName,
                  machineId: detail.machineId,
                  machineName: detail.machineName,
                  machineCode: detail.machineCode,
                  vehicleNumber: detail.vehicleNumber,
                  machineTypeId: detail.machineTypeId,
                  machineTypeName: detail.machineTypeName,
                  type: detail.type,
                  schema: useWoKpis ? [] : (machineTypeQuery.data?.customFields ?? []),
                }}
                submitting={updateMutation.isPending}
                errorMessage={errorMessage}
                onSaveDraft={handleSaveDraft}
                onSubmit={handleSubmit}
                hideKpiSection={useWoKpis}
                kpiSlot={kpiSlot ?? undefined}
                onUploadComplete={() => {
                  void queryClient.invalidateQueries({ queryKey: ['logsheet-maker', id] });
                }}
              />
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}
